import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createBlock as _createBlock, normalizeStyle as _normalizeStyle, createSlots as _createSlots, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "search-page" }
const _hoisted_2 = { class: "search-page__top" }
const _hoisted_3 = { class: "search-page__top-actions" }
const _hoisted_4 = { class: "search-page__main" }
const _hoisted_5 = { class: "search-page__content" }
const _hoisted_6 = { class: "search-page__content-top" }
const _hoisted_7 = { class: "shelf-form__platforms" }
const _hoisted_8 = { class: "search-page__filters" }
const _hoisted_9 = { class: "search-page__status" }
const _hoisted_10 = { class: "search-page__dropdown-item-left" }
const _hoisted_11 = ["src"]
const _hoisted_12 = { class: "search-page__status" }
const _hoisted_13 = { class: "search-page__side" }

import { ref, reactive, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { Plus, Search, ArrowDown, Check, Close } from "@element-plus/icons-vue";
import formatDate from "@/shared/lib/formatDate";
import {
  saveFilterState,
  loadFilterState,
  validateFilterState,
} from "@/shared/lib/filterStorage";
import { StatusTest } from "@/shared/ui/components/StatusTest";
import { EmptyData } from "@/shared/ui/components/EmptyData";
import { ActionInspector } from "@/shared/ui/components/ActionInspector";
import { ViewInspector } from "@/shared/ui/components/ViewInspector";
import { STATUSES_FILTER, STATUSES } from "../const";
import { ElMessage } from "element-plus";
import { FORM_MODE } from "@/shared/constants/mode";
import { cloneDeep } from "lodash";
import { ElMessageBox } from "element-plus";
import { IExperiment } from "@/shared/types/experiments";
import { IPlatform } from "@/shared/types/platform";
import { IVariant } from "@/shared/types/variants";


export default /*@__PURE__*/_defineComponent({
  __name: 'SearchtPage',
  setup(__props) {

const store = useStore();
const route = useRoute();
const router = useRouter();
const formMode = ref("");
const loading = ref(false);
const environment = ref("production");

const currentId = ref<number | null>(null);

const filter = reactive({
  search: "",
  status: new Set(),
  applications: new Set(),
  platforms: new Set(),
});

const isQaEngineer = computed(() => {
  return store.state.auth.roles.includes("qa_engineer");
});

const experiments = computed<IExperiment[]>(() => {
  return store.state.experiments.list;
});
const experiment = computed(() => {
  if (currentId.value) {
    const item =
      store.state.experiments.list.find(
        (experiment: IExperiment) => experiment.id === currentId.value
      ) || {};
    if (item) {
      item.platforms_ids = item?.platforms?.map((el: IPlatform) => el.id);
    }
    return item;
  } else {
    return {
      title: "",
      application_id: "",
      status: "draft",
      target: "",
      description: "",
      key: "",
      custom_status: "testing",
      variants: [
        {
          id: Date.now(),
          title: "",
          key: "",
          percentage: 0,
          status: "new",
        },
        {
          id: Date.now() + 1,
          title: "",
          key: "",
          percentage: 0,
          status: "new",
        },
      ],
      platforms_ids: [],
    };
  }
});
const statistic = computed(() => {
  return store.state.experiments.statistic;
});
const significance = computed(() => {
  return store.state.experiments.significance;
});
const applications = computed(() => {
  return store.state.applications.list;
});
const platforms = computed(() => {
  return store.state.platforms.list;
});
const isViewer = computed(() => {
  return store.state.auth.roles.includes("viewer");
});

const filteredList = computed(() => {
  const filtered = experiments.value.filter((item: IExperiment) => {
    if (
      filter.applications.size &&
      !filter.applications.has(item.application.id)
    ) {
      return false;
    }

    if (
      filter.platforms.size &&
      !item.platforms.some((platform) => filter.platforms.has(platform.id))
    ) {
      return false;
    }

    // If no status filters are selected, show all items
    if (filter.status.size === 0) {
      return true;
    }
    // Check if item's status is in the selected statuses
    return filter.status.has(item.custom_status);
  });
  if (!filter.search) {
    return filtered;
  }
  return filtered.filter((item) =>
    item.title.toLowerCase().includes(filter.search.toLowerCase())
  );
});

// Load saved filters after data is loaded
const loadSavedFilters = () => {
  const savedState = loadFilterState();
  if (savedState) {
    // Validate against available options
    const validStatuses = STATUSES_FILTER.map((s) => s.type);
    const availableApps = applications.value.map((app) => app.id);
    const availablePlatforms = platforms.value.map((platform) => platform.id);

    const validatedState = validateFilterState(
      savedState,
      availableApps,
      availablePlatforms,
      validStatuses
    );

    // Apply validated filters
    filter.search = validatedState.search;
    filter.status = validatedState.status; // This is already a Set from validateFilterState
    filter.applications = new Set(validatedState.applications);
    filter.platforms = new Set(validatedState.platforms);
  }
};

// Watch for filter changes and save to localStorage
watch(
  () => ({
    search: filter.search,
    status: Array.from(filter.status),
    applications: Array.from(filter.applications),
    platforms: Array.from(filter.platforms),
  }),
  () => {
    saveFilterState(filter);
  },
  { deep: true }
);

onMounted(async () => {
  try {
    environment.value = isQaEngineer.value ? "test" : "production";
    await getExperiments();
    if (experiments.value.length && experiments.value[0].id) {
      const experimentIdFromQuery: number | null = parseInt(
        route.query.experiment_id as string,
        10
      );
      let selectedExperiment: IExperiment =
        experiments.value.find((exp) => exp.id === experimentIdFromQuery) ||
        experiments.value[0];

      if (!route.query.experiment_id) {
        await router.replace({
          path: route.path,
          query: {
            ...route.query,
            experiment_id: selectedExperiment.id.toString(),
          },
        });
      }
      formMode.value = FORM_MODE.VIEW;

      currentId.value = selectedExperiment.id;

      try {
        await getApplications();
        await getPlatforms();

        // Load saved filters after applications and platforms are loaded
        loadSavedFilters();

        await fetchStatistics(selectedExperiment);
      } catch (e: any) {
        ElMessage({
          message: e,
          type: "error",
        });
      }
    }
  } catch (e: any) {
    ElMessage({
      message: e,
      type: "error",
    });
  }
});

const getExperiments = async () => {
  return await store.dispatch("experiments/getExperiments");
};
const getStatistics = async (payload: {
  experiment_id: number;
  created_at: string;
  environment: string;
}) => {
  return await store.dispatch("experiments/getStatistics", payload);
};
const getStatisticSignificance = async (payload: {
  experiment_id: number;
  environment: string;
}) => {
  return await store.dispatch("experiments/getStatisticSignificance", payload);
};
const getApplications = async () => {
  return await store.dispatch("applications/getApplications");
};
const getPlatforms = async () => {
  return await store.dispatch("platforms/getPlatforms");
};

const updateExperiments = async (id: number, field: { [key: string]: any }) => {
  return await store.dispatch("experiments/updateExperiments", { id, field });
};

const handleDeleteClick = async (id: number) => {
  ElMessageBox.confirm("", "Delete Super Buper Test?", {
    confirmButtonText: "Delete",
    cancelButtonText: "Cancel",
    cancelButtonClass: "cancel-button",
    confirmButtonClass: "confirm-button",
    showClose: false,
  }).then(async () => {
    await store.dispatch("experiments/deleteExperiments", id);
    onClickCeil(experiments.value[0]);
  });
};

const saveTest = (field: IExperiment) => {
  if (formMode.value === FORM_MODE.EDIT) {
    editTest(cloneDeep(field));
  } else {
    createTest({ ...cloneDeep(field), custom_status: "ready" });
  }
};
const createTest = async (val: IExperiment) => {
  try {
    const response = await store.dispatch("experiments/createExperiments", val);

    currentId.value = response.id;
    createVariant(response.id, val.variants);
    router.replace({
      path: route.path,
      query: { ...route.query, experiment_id: response.id },
    });

    // Switch to view mode and make sure the new experiment is selected
    formMode.value = FORM_MODE.VIEW;
    await getExperiments(); // Refresh experiments list to ensure new experiment is included
    onClickCeil(response, "new"); // Use the response which contains the newly created experiment
  } catch (e: any) {
    if (Array.isArray(e)) {
      e.forEach((error) => {
        ElMessage({
          message: error,
          type: "error",
        });
      });
    } else {
      ElMessage({
        message: e,
        type: "error",
      });
    }
  }
};
const editTest = async (val: IExperiment) => {
  try {
    loading.value = true;
    const response = await store.dispatch("experiments/editExperiments", {
      id: experiment.value.id,
      field: { ...val, custom_status: experiment.value.custom_status },
    });
    await createVariant(response.id, val.variants);
    await getStatistics({
      experiment_id: response.id,
      created_at: response.created_at,
      environment: environment.value,
    });
    formMode.value = FORM_MODE.VIEW;
  } catch (e: any) {
    if (Array.isArray(e)) {
      e.forEach((error) => {
        ElMessage({
          message: error,
          type: "error",
        });
      });
    } else {
      ElMessage({
        message: e,
        type: "error",
      });
    }
  } finally {
    loading.value = false;
  }
};

const createVariant = (experimentId: number, variants: IVariant[]) => {
  try {
    store
      .dispatch("experiments/createVariant", {
        experiment_id: experimentId,
        variants: variants.map((item) => {
          return {
            id: item.id,
            key: item.key,
            percentage: item.percentage,
            title: item.title,
          };
        }),
      })
      .then((response) => {
        experiment.value.variants = response.data.map((item: IVariant) => {
          return {
            id: item.id,
            title: item.title,
            key: item.key,
            percentage: item.percentage,
            status: "edit",
          };
        });
      });
  } catch (e: any) {
    ElMessage({
      message: e,
      type: "error",
    });
  }
};

const handleEdit = () => {
  formMode.value = FORM_MODE.EDIT;
};

const tableRowClassName = ({ row }: { row: IExperiment }) => {
  return row.id === currentId.value
    ? "table-row table-row--selected"
    : "table-row";
};

const addTest = () => {
  formMode.value = FORM_MODE.CREATE;
  currentId.value = null;
};

const handleTabChange = (env: string) => {
  environment.value = env;
  const experiment = experiments.value.find(
    (item: IExperiment) => item.id === currentId.value
  );
  if (experiment) {
    fetchStatistics(experiment);
  }
};

const onClickCeil = async (item: IExperiment, status?: string) => {
  formMode.value = FORM_MODE.VIEW;

  if (experiment.value.id !== item.id || status === "new") {
    try {
      currentId.value = item.id;
      environment.value = isQaEngineer.value ? "test" : "production";

      await fetchStatistics(item);
    } catch (e: any) {
      ElMessage({
        message: e,
        type: "error",
      });
    } finally {
      router.replace({
        path: route.path,
        query: { ...route.query, experiment_id: item.id },
      });
    }
  }
};

const fetchStatistics = async (item: IExperiment) => {
  await getStatistics({
    experiment_id: item.id,
    created_at: item.created_at,
    environment: environment.value,
  });
  await getStatisticSignificance({
    experiment_id: item.id,
    environment: environment.value,
  });
};

const getTitlePlatforms = (arr: IPlatform[] = []) => {
  if (arr.length === platforms.value.length) {
    return "All";
  }
  return arr.map((el) => el.title).join(", ");
};

const getTypeByStatus = (status: string) => {
  switch (status) {
    case "ready":
      return "primary";
    case "testing":
      return "success";
    case "done":
      return "info";
    default:
      return "primary";
  }
};

const onFilterPlatform = (id: number) => {
  if (filter.platforms.has(id)) {
    filter.platforms.delete(id);
  } else {
    filter.platforms.add(id);
  }
};

const onFilterApps = (id: number) => {
  if (filter.applications.has(id)) {
    filter.applications.delete(id);
  } else {
    filter.applications.add(id);
  }
};

const onFilterStatus = (statusType: string) => {
  if (filter.status.has(statusType)) {
    filter.status.delete(statusType);
  } else {
    filter.status.add(statusType);
  }
};

const enableViewMode = () => {
  formMode.value = FORM_MODE.VIEW;
};

return (_ctx: any,_cache: any) => {
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_dropdown_item = _resolveComponent("el-dropdown-item")!
  const _component_el_dropdown_menu = _resolveComponent("el-dropdown-menu")!
  const _component_el_dropdown = _resolveComponent("el-dropdown")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_table = _resolveComponent("el-table")!
  const _directive_loading = _resolveDirective("loading")!

  return _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[5] || (_cache[5] = _createElementVNode("div", { class: "search-page__title" }, "AB Testing", -1)),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_component_el_input, {
          "prefix-icon": _unref(Search),
          modelValue: filter.search,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((filter.search) = $event)),
          placeholder: "Search"
        }, null, 8, ["prefix-icon", "modelValue"]),
        _createVNode(_component_el_button, {
          type: "success",
          icon: _unref(Plus),
          disabled: isViewer.value,
          onClick: addTest
        }, {
          default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
            _createTextVNode("Add Test", -1)
          ]))]),
          _: 1
        }, 8, ["icon", "disabled"])
      ])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(platforms.value, (platform) => {
              return (_openBlock(), _createBlock(_component_el_button, {
                class: "shelf-form__platform",
                key: platform.id,
                size: "small",
                type: filter.platforms.has(platform.id) ? 'primary' : '',
                plain: "",
                onClick: ($event: any) => (onFilterPlatform(platform.id))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(platform.title), 1)
                ]),
                _: 2
              }, 1032, ["type", "onClick"]))
            }), 128))
          ]),
          _createElementVNode("div", _hoisted_8, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(STATUSES), (status) => {
              return (_openBlock(), _createBlock(_component_el_button, {
                class: "search-page__filter",
                key: status.type,
                size: "small",
                type: filter.status.has(status.type) ? 'primary' : '',
                plain: filter.status.has(status.type),
                text: !filter.status.has(status.type),
                bg: !filter.status.has(status.type),
                onClick: ($event: any) => (onFilterStatus(status.type))
              }, _createSlots({
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(status.title) + " ", 1)
                ]),
                _: 2
              }, [
                (status.color)
                  ? {
                      name: "icon",
                      fn: _withCtx(() => [
                        _createVNode(_component_el_icon, { size: "12" }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", {
                              class: "circle",
                              style: _normalizeStyle(`background: ${status.color}`)
                            }, null, 4)
                          ]),
                          _: 2
                        }, 1024)
                      ]),
                      key: "0"
                    }
                  : undefined
              ]), 1032, ["type", "plain", "text", "bg", "onClick"]))
            }), 128)),
            (filter.status.size > 0)
              ? (_openBlock(), _createBlock(_component_el_button, {
                  key: 0,
                  size: "small",
                  text: "",
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (filter.status = new Set()))
                }, {
                  default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                    _createTextVNode("Clear", -1)
                  ]))]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ])
        ]),
        _createVNode(_component_el_table, {
          data: filteredList.value,
          height: "100%",
          style: {"width":"100%","border-radius":"4px"},
          "row-class-name": tableRowClassName,
          onRowClick: onClickCeil
        }, {
          empty: _withCtx(() => [
            _createVNode(_unref(EmptyData), { class: "search-page__table-empty" })
          ]),
          default: _withCtx(() => [
            _createVNode(_component_el_table_column, {
              prop: "status",
              label: "",
              width: "44"
            }, {
              default: _withCtx((scope) => [
                _createVNode(_unref(StatusTest), {
                  status: scope.row.status
                }, null, 8, ["status"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_table_column, {
              prop: "title",
              label: "Test name",
              sortable: ""
            }),
            _createVNode(_component_el_table_column, {
              prop: "key",
              label: "Unique Key",
              sortable: ""
            }),
            _createVNode(_component_el_table_column, {
              prop: "application",
              label: "All Apps",
              sortable: "",
              "label-class-name": "search-page__header-column"
            }, {
              header: _withCtx(() => [
                _createElementVNode("div", _hoisted_9, [
                  _createTextVNode(_toDisplayString(filter.applications.size || "All") + " Apps ", 1),
                  (filter.applications.size)
                    ? (_openBlock(), _createBlock(_component_el_icon, {
                        key: 0,
                        class: "search-page__status-close",
                        onClick: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (filter.applications = new Set()), ["stop"]))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(Close))
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  _createVNode(_component_el_dropdown, {
                    trigger: "click",
                    onCommand: onFilterApps
                  }, {
                    dropdown: _withCtx(() => [
                      _createVNode(_component_el_dropdown_menu, { style: {"width":"256px"} }, {
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(applications.value, (application) => {
                            return (_openBlock(), _createBlock(_component_el_dropdown_item, {
                              key: application.id,
                              command: application.id,
                              class: _normalizeClass(["search-page__dropdown-item", {
                          'search-page__dropdown-item_active':
                            filter.applications.has(application.id),
                        }])
                            }, {
                              default: _withCtx(() => [
                                _createElementVNode("div", _hoisted_10, [
                                  _createElementVNode("img", {
                                    src: application.icon_full_url,
                                    class: "search-page__dropdown-item-image"
                                  }, null, 8, _hoisted_11),
                                  _createElementVNode("span", null, _toDisplayString(application.title), 1)
                                ]),
                                _createVNode(_component_el_icon, {
                                  color: "#409EFF",
                                  class: "search-page__dropdown-item-icon"
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_unref(Check))
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 2
                            }, 1032, ["command", "class"]))
                          }), 128))
                        ]),
                        _: 1
                      })
                    ]),
                    default: _withCtx(() => [
                      _createVNode(_component_el_icon, {
                        class: "search-page__status-arrow",
                        color: "#909399",
                        onClick: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"]))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(ArrowDown))
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ])
              ]),
              default: _withCtx((scope) => [
                _createTextVNode(_toDisplayString(scope.row.application.title), 1)
              ]),
              _: 1
            }),
            _createVNode(_component_el_table_column, {
              prop: "platforms",
              label: "OS",
              "label-class-name": "search-page__header-column"
            }, {
              default: _withCtx((scope) => [
                _createTextVNode(_toDisplayString(getTitlePlatforms(scope.row.platforms)), 1)
              ]),
              _: 1
            }),
            _createVNode(_component_el_table_column, {
              prop: "created_at",
              label: "Start Date",
              sortable: ""
            }, {
              default: _withCtx((scope) => [
                _createTextVNode(_toDisplayString(_unref(formatDate)(scope.row.created_at)), 1)
              ]),
              _: 1
            }),
            _createVNode(_component_el_table_column, {
              prop: "custom_status",
              label: "All Statuses",
              "class-name": "search-page__all-status",
              sortable: ""
            }, {
              header: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                _createTextVNode(" All Statuses ", -1)
              ]))]),
              default: _withCtx((scope) => [
                _createElementVNode("div", _hoisted_12, [
                  _createVNode(_component_el_tag, {
                    class: "search-page__tag",
                    type: getTypeByStatus(scope.row.custom_status)
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(scope.row.custom_status || "Ready"), 1)
                    ]),
                    _: 2
                  }, 1032, ["type"]),
                  _createVNode(_component_el_dropdown, {
                    trigger: "click",
                    disabled: isViewer.value,
                    onCommand: ($event: any) => (
                    updateExperiments(scope.row.id, { custom_status: $event })
                  )
                  }, {
                    dropdown: _withCtx(() => [
                      _createVNode(_component_el_dropdown_menu, null, {
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(STATUSES), (status) => {
                            return (_openBlock(), _createBlock(_component_el_dropdown_item, {
                              key: status.type,
                              command: status.type,
                              disabled: status.type == scope.row.custom_status
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(status.title), 1)
                              ]),
                              _: 2
                            }, 1032, ["command", "disabled"]))
                          }), 128))
                        ]),
                        _: 2
                      }, 1024)
                    ]),
                    default: _withCtx(() => [
                      _createVNode(_component_el_icon, {
                        class: "search-page__status-arrow",
                        color: "#909399"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(ArrowDown))
                        ]),
                        _: 1
                      })
                    ]),
                    _: 2
                  }, 1032, ["disabled", "onCommand"])
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["data"])
      ]),
      _createElementVNode("div", _hoisted_13, [
        (formMode.value === _unref(FORM_MODE).VIEW)
          ? (_openBlock(), _createBlock(_unref(ViewInspector), {
              key: 0,
              id: currentId.value,
              status: formMode.value,
              field: experiment.value,
              statistic: statistic.value,
              significance: significance.value,
              environment: environment.value,
              onDelete: handleDeleteClick,
              onEdit: handleEdit,
              onOnTabChange: handleTabChange
            }, null, 8, ["id", "status", "field", "statistic", "significance", "environment"]))
          : (
            formMode.value === _unref(FORM_MODE).CREATE || formMode.value === _unref(FORM_MODE).EDIT
          )
            ? (_openBlock(), _createBlock(_unref(ActionInspector), {
                key: 1,
                status: formMode.value,
                field: experiment.value,
                onSubmit: saveTest,
                onDelete: handleDeleteClick,
                onCancel: enableViewMode
              }, null, 8, ["status", "field"]))
            : _createCommentVNode("", true)
      ])
    ])
  ])), [
    [_directive_loading, loading.value]
  ])
}
}

})